import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { Eye, EyeOff, FolderOpen } from 'lucide-react';

const LoginPage = () => {
  const [isLogin, setIsLogin] = useState(!new URLSearchParams(window.location.search).has('signup'));
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const { login, register, loginWithGoogle } = useAuth();
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    email: '',
    password: '',
    name: '',
    company_name: '',
    role: 'Owner'
  });

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (isLogin) {
        const userData = await login(formData.email, formData.password);
        toast.success('Welcome back!');
        navigate(userData.onboarding_complete === false ? '/onboarding' : '/dashboard');
      } else {
        await register(formData);
        toast.success('Account created successfully!');
        navigate('/onboarding');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div 
      className="min-h-screen bg-zinc-950 flex items-center justify-center p-4"
      data-testid="login-page"
    >
      {/* Grid background */}
      <div className="fixed inset-0 opacity-[0.04]"
        style={{
          backgroundImage: `repeating-linear-gradient(0deg, transparent, transparent 39px, rgba(255,196,12,0.3) 39px, rgba(255,196,12,0.3) 40px),
            repeating-linear-gradient(90deg, transparent, transparent 39px, rgba(255,196,12,0.3) 39px, rgba(255,196,12,0.3) 40px)`
        }}
      />

      <div className="w-full max-w-sm relative z-10">
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 mb-3">
            <div className="bg-primary p-2">
              <FolderOpen className="w-6 h-6 text-black" />
            </div>
            <h1 className="font-heading text-2xl font-bold uppercase tracking-wider text-white">Punchlist</h1>
          </div>
          <p className="text-zinc-500 text-sm">Project Management for MEP Contractors</p>
        </div>

        {/* Form Card */}
        <div className="bg-white border border-zinc-200 p-6">
          {/* Tabs */}
          <div className="flex mb-6 border border-zinc-200">
            <button
              type="button"
              onClick={() => setIsLogin(true)}
              data-testid="login-tab"
              className={`flex-1 py-2.5 font-heading font-semibold text-xs uppercase tracking-wider transition-colors ${
                isLogin ? 'bg-primary text-black' : 'bg-white text-zinc-500 hover:text-zinc-700'
              }`}
            >
              Login
            </button>
            <button
              type="button"
              onClick={() => setIsLogin(false)}
              data-testid="signup-tab"
              className={`flex-1 py-2.5 font-heading font-semibold text-xs uppercase tracking-wider border-l border-zinc-200 transition-colors ${
                !isLogin ? 'bg-primary text-black' : 'bg-white text-zinc-500 hover:text-zinc-700'
              }`}
            >
              Sign Up
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            {!isLogin && (
              <>
                <div>
                  <label className="block text-[10px] font-heading font-semibold uppercase tracking-[0.15em] text-zinc-500 mb-1.5">Full Name</label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required={!isLogin}
                    data-testid="name-input"
                    className="w-full h-11 border border-zinc-200 bg-white text-zinc-900 text-sm px-3 focus:outline-none focus:border-primary transition-colors"
                    placeholder="John Smith"
                  />
                </div>

                <div>
                  <label className="block text-[10px] font-heading font-semibold uppercase tracking-[0.15em] text-zinc-500 mb-1.5">Company Name</label>
                  <input
                    type="text"
                    name="company_name"
                    value={formData.company_name}
                    onChange={handleChange}
                    required={!isLogin}
                    data-testid="company-name-input"
                    className="w-full h-11 border border-zinc-200 bg-white text-zinc-900 text-sm px-3 focus:outline-none focus:border-primary transition-colors"
                    placeholder="ABC Contractors"
                  />
                </div>

                <div>
                  <label className="block text-[10px] font-heading font-semibold uppercase tracking-[0.15em] text-zinc-500 mb-1.5">Role</label>
                  <select
                    name="role"
                    value={formData.role}
                    onChange={handleChange}
                    data-testid="role-select"
                    className="w-full h-11 border border-zinc-200 bg-white text-zinc-900 text-sm px-3 focus:outline-none focus:border-primary transition-colors"
                  >
                    <option value="Owner">Owner</option>
                    <option value="Project Manager">Project Manager</option>
                    <option value="Foreman">Foreman</option>
                  </select>
                </div>
              </>
            )}

            <div>
              <label className="block text-[10px] font-heading font-semibold uppercase tracking-[0.15em] text-zinc-500 mb-1.5">Email</label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
                data-testid="email-input"
                className="w-full h-11 border border-zinc-200 bg-white text-zinc-900 text-sm px-3 focus:outline-none focus:border-primary transition-colors"
                placeholder="you@company.com"
              />
            </div>

            <div>
              <label className="block text-[10px] font-heading font-semibold uppercase tracking-[0.15em] text-zinc-500 mb-1.5">Password</label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  required
                  data-testid="password-input"
                  className="w-full h-11 border border-zinc-200 bg-white text-zinc-900 text-sm px-3 pr-11 focus:outline-none focus:border-primary transition-colors"
                  placeholder="Enter password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-700"
                  data-testid="toggle-password"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              data-testid="submit-btn"
              className="w-full h-11 bg-primary text-black font-heading font-bold uppercase tracking-wider text-sm hover:bg-primary-hover transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Loading...' : isLogin ? 'Login' : 'Create Account'}
            </button>
          </form>

          <div className="flex items-center my-5">
            <div className="flex-1 h-px bg-zinc-200"></div>
            <span className="px-3 text-[10px] font-heading font-semibold uppercase tracking-wider text-zinc-400">or</span>
            <div className="flex-1 h-px bg-zinc-200"></div>
          </div>

          <button
            type="button"
            onClick={loginWithGoogle}
            data-testid="google-login-btn"
            className="w-full h-11 bg-white text-zinc-700 font-medium text-sm border border-zinc-200 hover:bg-zinc-50 transition-colors flex items-center justify-center gap-2"
          >
            <svg className="w-4 h-4" viewBox="0 0 24 24">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
            </svg>
            Continue with Google
          </button>

          <p className="text-center mt-5 text-xs text-zinc-400">
            Have an invite code?{' '}
            <Link to="/join" className="text-zinc-700 font-semibold hover:text-primary transition-colors" data-testid="join-link">
              Join a Company
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
